import { apiClient } from '@api/apiClient';
import { Button, CircularProgress } from '@atgfr8/atg-ui-tailwind';
import { ToastType } from '@components/Toast';
import { closeModal } from '@store/modals/store';
import { TabState } from '@store/orders';
import { setActiveCarrierTab } from '@store/orders/actions/carrierActions';
import {
  loadSureStore,
  orderStore,
  setLoadSureStore,
  setOrderStore,
} from '@store/orders/store';
import { handleToast } from '@utils/utils';
import { createSignal } from 'solid-js';
import { produce } from 'solid-js/store';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import {
  cancelInsuranceCbModal,
  cancelReasons,
  loadId,
  loadSureVendorContractId,
} from './LoadSureHelpers';
import { LoadSureSelect } from './LoadSureSelect';

export const CancelInsuranceCbModal = () => {
  const [cancelReason, setCancelReason] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);

  const handleCancel = async () => {
    setLoading(true);
    try {
      if (loadSureStore.callback) {
        loadSureStore.callback();
        setLoadSureStore('callback', null);
      }
      await apiClient.post(
        '/LoadSure/CancelInsurance',
        {
          reasonCode: cancelReason(),
          loadId: loadId(),
          vendorContractId: loadSureVendorContractId(),
        },
        buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsInsurance,
        ]),
      );
      const vendorIndex = orderStore.order.vendors?.findIndex(
        (v) => v.id === loadSureVendorContractId(),
      );
      setOrderStore(
        'order',
        'loads',
        orderStore.activeTab.index,
        'loadSureVendorContractId',
        null,
      );
      setOrderStore(
        'order',
        'vendors',
        produce((vendors) => {
          if (vendorIndex !== undefined && vendorIndex >= 0) {
            vendors?.splice(vendorIndex, 1);
          }
        }),
      );
      setLoadSureStore('isDirty', []);
      setOrderStore(
        'order',
        'loads',
        orderStore.activeTab.index,
        'loadSureCertificate',
        null,
      );
      setActiveCarrierTab({ index: 0, type: 'load' } as TabState);
      handleToast(ToastType.Success, 'Insurance cancelled successfully');
      loadSureStore.successCallback?.();
    } catch (error: unknown) {
      if (Array.isArray(error)) {
        error.forEach((err) => {
          handleToast(
            ToastType.Error,
            (err as { errorMessage: string }).errorMessage,
          );
        });
      } else {
        handleToast(ToastType.Error, 'Failed to cancel insurance');
      }
    } finally {
      setLoadSureStore('successCallback', null);
      setLoading(false);
      closeModal(cancelInsuranceCbModal);
    }
  };

  return (
    <div>
      <div class="text-xl font-semibold mb-2">Confirm Policy Cancellation</div>
      <div class="text-sm font-semibold">Insurance Cancellation Required</div>
      <div class="text-sm mb-2">
        You must cancel the current policy before removing the Carrier or
        voiding the load. Select a cancellation reason to continue.
      </div>
      <div class="my-4 ">
        <LoadSureSelect
          label="Reason for Cancellation"
          value={null}
          onChange={(e: { target: { value: string } }) => {
            setCancelReason(e.target.value);
          }}
          required={true}
          options={cancelReasons.map((c) => ({
            id: c.id,
            name: c.name,
          }))}
        />
        <div class="flex gap-x-2 float-right mt-4">
          <Button
            onClick={handleCancel}
            variant="contained"
            color="info"
            endIcon={loading() ? <CircularProgress size={20} /> : null}
            disabled={cancelReason() === null}
            dataTestId="cancel-insurance-continue-button"
          >
            Cancel Policy and Continue
          </Button>
          <Button
            color="info"
            dataTestId="cancel-insurance-cancel-button"
            onClick={() => {
              setLoadSureStore('callback', null);
              setLoadSureStore('successCallback', null);
              closeModal(cancelInsuranceCbModal);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
